import { axiosInstance } from './axios';
import { ERPUserType, UserType } from '@admin-panel/types';
import { SearchFilter } from '../utils';
import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { MetaQuery } from '@refinedev/core';

const ERP_USER_API_URL = GATEWAY_API_URL + '/identity/erpuser';

const getPortalCodeForUserType = (userType: UserType) => {
  switch (userType) {
    case 'admin':
      return 'erp';
    case 'storestaff':
      return 'pos';
    case 'vendor':
      return 'vendor';
  }
};

const normalizePortalCode = (value?: string) => value?.toLowerCase();

export class ERPUserAPIClient extends CrudApiClient {
  private userType: UserType;

  constructor(userType: UserType) {
    super(ERP_USER_API_URL);
    this.userType = userType;
  }

  transformNode(node: any) {
    if (node) {
      const roleIdsByPortal = (node.roles || []).reduce((acc: Record<string, number[]>, role: any) => {
        const portalCode = normalizePortalCode(role.portalCode);
        if (!portalCode || role.id === undefined || role.id === null) {
          return acc;
        }
        if (!acc[portalCode]) {
          acc[portalCode] = [];
        }
        acc[portalCode].push(role.id);
        return acc;
      }, {});

      node.roleIdsByPortal = roleIdsByPortal;
      node.roleIds = roleIdsByPortal[getPortalCodeForUserType(this.userType)] || [];
    }
    return node;
  }

  getERPUserType() {
    switch (this.userType) {
      case 'storestaff':
      case 'admin':
        return ERPUserType.internal;
      case 'vendor':
        return ERPUserType.vendor;
    }
  }

  async search(query: any, page: number, size: number, meta: MetaQuery | undefined): Promise<any> {
    try {
      if (!query) {
        query = {};
      }
      if (!query.filters) query.filters = [];

      query.filters.push({
        field: 'userType',
        filterType: 'EQ',
        value: this.getERPUserType(),
      });
      if (this.userType == 'vendor') {
        const vendorIdFilter = query.filters.find((f: SearchFilter) => f.field === 'vendorId');
        const emailFilter = query.filters.find((f: SearchFilter) => f.field === 'email');
        if (!vendorIdFilter && !emailFilter) {
          return [];
        }
      }
      return await super.search(query, page, size, meta);
    } catch (error) {
      console.error(`Error searching entities with Query:`, query, error);
      throw error;
    }
  }

  async create(payload: any, meta: MetaQuery | undefined): Promise<any> {
    try {
      if (this.userType == 'vendor' || payload.password) {
        const response = await axiosInstance.post(`${ERP_USER_API_URL}/external/`, payload);
        return response.data.data;
      } else {
        return super.create(payload, meta);
      }
    } catch (error) {
      console.error(`Error creating entity:`, error);
      throw error;
    }
  }

  async resetPassword(
    id: number | string,
    payload: { password: string; notify?: boolean },
  ): Promise<any> {
    try {
      const response = await axiosInstance.post(`${ERP_USER_API_URL}/${id}/reset-password`, payload);
      return response.data.data;
    } catch (error) {
      console.error(`Error resetting ERP user password for user ${id}:`, error);
      throw error;
    }
  }

  async deactivate(id: number | string, payload: { reason: string }): Promise<any> {
    try {
      const response = await axiosInstance.post(`${ERP_USER_API_URL}/${id}/deactivate`, payload);
      return response.data.data;
    } catch (error) {
      console.error(`Error deactivating ERP user ${id}:`, error);
      throw error;
    }
  }

  async createVendorWithCredentials(payload: {
    password: string;
    notify?: boolean;
    userEntry: any;
  }): Promise<any> {
    try {
      const response = await axiosInstance.post(`${ERP_USER_API_URL}/external/`, payload);
      return response.data.data;
    } catch (error) {
      console.error('Error creating vendor user with credentials:', error);
      throw error;
    }
  }

  async searchByUserRole(payload: {
    roles: Array<string>;
    app?: string;
    storeSalesChannelId?: string | number;
    warehouseId?: string | number;
    vendorId?: string | number;
    salesChannelId?: string | number;
    searchText?: string;
  }): Promise<any> {
    try {
      const response = await axiosInstance.post(`${GATEWAY_API_URL}/search-by-user-role/`, payload);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error searching users by role:', payload, error);
      throw error;
    }
  }
}

export default ERPUserAPIClient;
